import React, { useState, useEffect, useRef } from 'react';
import { 
  Lock, User as UserIcon, ArrowRight, AlertCircle, Loader2, Cpu, 
  Fingerprint, Eye, EyeOff, Terminal, Zap, Activity, Radio, 
  Command, Settings, X
} from 'lucide-react';

interface LoginProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
}

const SYSTEM_LOGS = [
  "SHIV_CORE_V2.5 // INITIALIZING",
  "PROTOCOL_STACK_GATEWAY: 0x8842",
  "AES_256_GCM_ENCRYPTION: ENABLED",
  "NEURAL_HANDSHAKE: SYNC_READY",
  "IDENTITY_VAULT: ONLINE",
  "AUTH_PENDING: CREDENTIALS_REQ"
];

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [logIndex, setLogIndex] = useState(0);
  const [bootSequence, setBootSequence] = useState(true);
  const [showRecovery, setShowRecovery] = useState(false);
  
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setBootSequence(false), 1500);
    
    const interval = setInterval(() => {
      setLogIndex((prev) => (prev + 1) % SYSTEM_LOGS.length);
    }, 2500);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
    };
  }, []);

  const handleAuth = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isAuthenticating) return;
    
    const cleanUser = username.trim();
    const cleanPass = password;

    if (!cleanUser || !cleanPass) {
      setError("SECURITY_FAULT: MISSING_CREDENTIALS");
      return;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      const success = await onLogin(cleanUser, cleanPass);
      if (!success) {
        setError("AUTH_FAILURE: ACCESS_DENIED");
        setIsAuthenticating(false);
      }
    } catch (err) {
      setError("CORE_EXCEPTION: HANDSHAKE_TIMEOUT");
      setIsAuthenticating(false);
    }
  };

  const handleSystemReset = () => {
    if (window.confirm("CRITICAL: This will clear all local data and restore default system settings. Proceed?")) {
        localStorage.clear();
        window.location.reload();
    }
  };

  if (bootSequence) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-8 overflow-hidden">
        <div className="w-full max-w-md relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 blur-[120px] rounded-full animate-pulse"></div>
          <div className="relative z-10 flex flex-col items-center">
            <div className="relative mb-12">
              <div className="w-24 h-24 border-2 border-emerald-500/20 rounded-full animate-ping opacity-25"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                   <Cpu className="text-emerald-500 animate-pulse" size={48} />
                   <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-bounce"></div>
                </div>
              </div>
            </div>
            <div className="w-full space-y-6">
                <div className="flex justify-between items-end mb-2">
                    <span className="text-[10px] font-mono text-emerald-500/60 uppercase tracking-[0.4em]">Establishing_System_Sync</span>
                    <span className="text-[10px] font-mono text-emerald-400">98%</span>
                </div>
                <div className="w-full bg-emerald-950/30 h-1.5 rounded-full overflow-hidden border border-white/5 backdrop-blur-md">
                   <div className="h-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.5)] animate-progress origin-left"></div>
                </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#020617] relative overflow-hidden font-sans selection:bg-emerald-500/30">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.05),transparent_70%)]"></div>
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'linear-gradient(rgba(16,185,129,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-[#020617]/50 to-[#020617]"></div>
      </div>
      
      <div className="flex-1 flex flex-col lg:flex-row items-stretch justify-center relative z-10">
        {/* Left Brand Section */}
        <div className="hidden lg:flex lg:w-5/12 p-20 flex-col justify-between relative overflow-hidden">
          <div className="absolute top-1/4 -left-12 w-64 h-64 bg-indigo-500/10 blur-[120px] rounded-full"></div>
          
          <div className="space-y-16 relative z-10">
            <div className="flex items-center gap-5 group">
              <div className="w-16 h-16 bg-emerald-500/10 backdrop-blur-3xl border border-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.1)] group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                <Command size={36} strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-white font-black text-4xl tracking-tighter uppercase leading-none italic">SHIV ERP</h1>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_#10b981]"></div>
                  <span className="text-[10px] font-mono text-emerald-500/60 uppercase tracking-[0.4em] font-bold">Terminal_V2.5_Stable</span>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="inline-flex items-center gap-3 px-5 py-2 bg-emerald-500/5 border border-emerald-500/20 rounded-full text-emerald-400 text-[10px] font-black uppercase tracking-[0.25em] shadow-[0_0_25px_rgba(16,185,129,0.05)]">
                <Radio size={14} className="animate-pulse" /> Core_Link: [SECURE]
              </div>
              <h2 className="text-5xl xl:text-6xl font-black text-white leading-[0.9] tracking-tighter uppercase">
                Enterprise<br /><span className="text-emerald-500">Control.</span>
              </h2>
              <p className="text-slate-400 text-xl font-medium leading-relaxed max-w-sm">
                Unified resource orchestration for global infrastructure and logistical nodes.
              </p>
            </div>
          </div>

          <div className="space-y-8 relative z-10">
             <div className="flex items-center gap-5">
                <Terminal size={24} className="text-emerald-500/50" />
                <div className="bg-black/40 backdrop-blur-3xl px-8 py-6 rounded-[2rem] border border-white/5 w-full max-w-sm shadow-2xl">
                    <p className="text-emerald-500/30 font-mono text-[10px] mb-2 flex items-center gap-2 uppercase tracking-widest">
                       <Activity size={10} /> Status_Stream
                    </p>
                    <p className="text-emerald-400 font-mono text-xs animate-in fade-in slide-in-from-left-2 duration-500 font-bold">
                      {SYSTEM_LOGS[logIndex]}
                    </p>
                </div>
             </div>
          </div>
        </div>

        {/* Right Auth Section */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
          <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="bg-slate-900/40 backdrop-blur-3xl p-10 lg:p-14 rounded-[3.5rem] border border-white/10 shadow-[0_30px_100px_rgba(0,0,0,0.5)] relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none transition-all group-hover:bg-emerald-500/10"></div>
              
              <div className="mb-12 relative z-10">
                <div className="flex items-center justify-between mb-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-[2rem] flex items-center justify-center shadow-[0_20px_40px_rgba(16,185,129,0.3)] ring-4 ring-emerald-500/10 group-hover:rotate-6 transition-transform duration-500">
                    <Fingerprint className="text-white" size={40} />
                  </div>
                  <button 
                      type="button"
                      onClick={() => setShowRecovery(!showRecovery)}
                      className="p-2 text-slate-600 hover:text-emerald-400 transition-colors"
                      title="System Recovery"
                  >
                      <Settings size={20} />
                  </button>
                </div>
                <h3 className="text-4xl font-black text-white tracking-tighter uppercase italic">Access Gateway</h3>
              </div>

              {/* Recovery UI */}
              {showRecovery && (
                <div className="mb-8 p-6 bg-rose-500/10 border border-rose-500/20 rounded-3xl animate-in zoom-in-95">
                    <div className="flex items-center gap-3 mb-4 text-rose-400">
                        <AlertCircle size={20} />
                        <span className="text-[10px] font-black uppercase tracking-widest">System Recovery Tools</span>
                    </div>
                    <button 
                        onClick={handleSystemReset}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-rose-600/20 hover:bg-rose-600/40 text-rose-400 border border-rose-500/30 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all"
                    >
                        Restore Global Settings
                    </button>
                </div>
              )}

              <form onSubmit={handleAuth} className="space-y-8 relative z-10">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] ml-3">Registry_Username</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none text-slate-600 group-focus-within:text-emerald-500 transition-colors">
                      <UserIcon size={22} strokeWidth={1.5} />
                    </div>
                    <input 
                      type="text"
                      autoComplete="username"
                      disabled={isAuthenticating}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-16 pr-6 py-5 bg-white/5 border border-white/10 rounded-[1.75rem] text-sm font-bold text-white outline-none transition-all focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-4 focus:ring-emerald-500/5 placeholder:text-slate-800"
                      placeholder="Personnel_ID"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] ml-3">Security_Token</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none text-slate-600 group-focus-within:text-emerald-500 transition-colors">
                      <Lock size={22} strokeWidth={1.5} />
                    </div>
                    <input 
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      disabled={isAuthenticating}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-16 pr-16 py-5 bg-white/5 border border-white/10 rounded-[1.75rem] text-sm font-bold text-white outline-none transition-all focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-4 focus:ring-emerald-500/5 placeholder:text-slate-800"
                      placeholder="Access_Code"
                    />
                    <button
                      type="button"
                      disabled={isAuthenticating}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-7 flex items-center text-slate-600 hover:text-emerald-400 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="p-5 bg-rose-500/10 border border-rose-500/20 rounded-3xl flex items-start gap-4 animate-in shake duration-500">
                    <AlertCircle size={22} className="text-rose-500 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mb-1">Authorization_Fault</p>
                        <p className="text-xs font-bold text-rose-200/80 leading-relaxed uppercase break-words">{error}</p>
                    </div>
                    <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-200">
                        <X size={14} />
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isAuthenticating}
                  className={`w-full flex items-center justify-center gap-4 py-6 rounded-[2rem] font-black text-[13px] uppercase tracking-[0.3em] transition-all active:scale-[0.98] shadow-2xl relative overflow-hidden group ${
                    isAuthenticating 
                    ? 'bg-slate-800 text-slate-500 cursor-wait' 
                    : 'bg-emerald-500 text-[#020617] hover:bg-emerald-400 shadow-emerald-500/20 hover:-translate-y-1'
                  }`}
                >
                  {isAuthenticating ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Syncing_Node...
                    </>
                  ) : (
                    <>
                      Login
                      <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform duration-500" />
                    </>
                  )}
                  {!isAuthenticating && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer pointer-events-none"></div>
                  )}
                </button>
              </form>
            </div>
            <div className="mt-8 text-center">
                <p className="text-[9px] font-black text-slate-700 uppercase tracking-[0.4em]">Integrated_Network_Core // SHIV_ERP_2.5</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
        @keyframes progress {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        .animate-progress {
          animation: progress 2.2s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Login;